/**
 * wa-continuity — website-agent continuity across FULL page loads (FLO-524).
 *
 * The website agent is a MULTI-PAGE widget: a visitor reading a marketing site
 * crosses several full document loads, and every one of them used to reset the
 * rail to its launcher and throw the conversation away. This module is the whole
 * persistence layer for fixing that — three `sessionStorage` keys, read once at
 * boot and written on change:
 *
 *   floe_wa_open         '1' while the sidebar is open (absent = closed)
 *   floe_wa_transcript   JSON array, the last {@link MAX_PERSISTED_MESSAGES} turns
 *   floe_wa_conversation the session id that produced that transcript
 *
 * `sessionStorage`, not `localStorage`, BY DESIGN: continuity is scoped to this
 * tab's visit. A new tab, or a return tomorrow, starts fresh — an agent that
 * re-opens itself with last week's chat is a stalker, not a host. (Contrast
 * `floe_anonymous_id`, which is deliberately localStorage: identity is long-lived,
 * an open panel is not.)
 *
 * ── What restore does NOT do ────────────────────────────────────────────────
 * Restoring is VISUAL ONLY. It paints the rail open with the persisted turns and
 * starts NO pipecat session: a session costs ~$0.33, so auto-starting one per
 * pageview would turn a visitor reading five pages into $1.65 of nothing. The
 * first real interaction (a send, a chip, the CTA, the mic) boots the session —
 * see {@link lazyStartAction}.
 *
 * The SERVER's copy of the conversation is the other half, and it is now wired:
 * FLO-544 calls `POST /sites/web-agent/session` (FLO-329's open/resume) before
 * the pipecat boot and threads the returned conversation id through
 * `/sites/start-session` — see `wa-resume.ts`, which also owns the visitor
 * identity key. When that call resumes a thread that HAS turns, they replace
 * what this module restored. Two limits keep the local transcript load-bearing
 * rather than legacy: the round trip can fail (offline, timeout, 4xx) and must
 * degrade to exactly the behaviour above, and no producer writes `ChatMessage`
 * rows yet (the bot does not consume the forwarded id), so today every resumed
 * conversation comes back EMPTY and this storage is still the only history a
 * returning visitor sees.
 *
 * `floe_wa_conversation` therefore holds the server's conversation id once one
 * is known, and the pipecat session id until then — an opaque thread identifier
 * either way, for forensics; it is never sent back as a resume token (the server
 * resolves the thread from the visitor, never from a client-supplied id).
 *
 * Every storage call is wrapped: `sessionStorage` throws on access in some
 * privacy modes, is absent under SSR/tests, and `setItem` throws on quota. A
 * blocked or full store must degrade to "no continuity", never to a broken
 * agent, so nothing here throws and nothing here returns a rejected promise.
 */

/** Sidebar open-state flag. Cross-package contract (FLO-524). */
export const WA_OPEN_KEY = 'floe_wa_open';
/** Capped transcript tail. Cross-package contract (FLO-524). */
export const WA_TRANSCRIPT_KEY = 'floe_wa_transcript';
/**
 * Conversation/session identifier for the persisted transcript (FLO-524).
 *
 * Holds the server's FLO-329 conversation id once the resume call has returned
 * one (FLO-544), and the pipecat session id before that. Tab-scoped like the
 * other two keys; the visitor IDENTITY that outlives the tab lives in
 * localStorage under `floe_wa_visitor` (wa-resume.ts), which is also where the
 * two-tab semantics are documented.
 */
export const WA_CONVERSATION_KEY = 'floe_wa_conversation';

/**
 * The conversation the visitor ENDED (FLO-544).
 *
 * End is "forget this chat", and the local half of that is easy — drop the
 * transcript and the id. The server half is not ours to drop: `openOrResume`
 * resolves a thread from the VISITOR (newest ACTIVE/IDLE conversation inside the
 * 120-minute window), not from any id the browser holds, and nothing finalizes a
 * conversation from the client. So the very next open hands back the same
 * conversation — and, once a producer writes `ChatMessage` rows, its history,
 * which `reconcileRestoredMessages` would paint straight back over the chat the
 * visitor just deleted. This key is what stops that: the id is remembered when
 * it is forgotten, and a resume that returns it contributes no turns.
 *
 * Tab-scoped like the rest of this module, because the intent is: the surface
 * the visitor cleared stays cleared. See the SDK's `applyOpenedConversation` for
 * what it does NOT cover — the session stays linked, so the operator's record of
 * the conversation continues rather than vanishing mid-chat.
 */
export const WA_FORGOTTEN_KEY = 'floe_wa_forgotten';

/** The only value ever written to {@link WA_OPEN_KEY}; absence means closed. */
export const WA_OPEN_VALUE = '1';

/**
 * How many turns survive a page load. The tail, not the head: a returning
 * visitor needs the last exchange for context, not the opening pleasantries.
 */
export const MAX_PERSISTED_MESSAGES = 25;

/**
 * Per-message content ceiling. sessionStorage is a ~5MB budget SHARED with the
 * customer's own page, and a long RAG answer can run thousands of characters —
 * 25 of those would be an antisocial neighbour and would start tripping the
 * quota path (which silently drops the write, i.e. loses the whole transcript).
 * Truncating one long answer's tail is the cheaper loss.
 */
export const MAX_PERSISTED_CONTENT_CHARS = 4000;

// Type-only (erased at compile time) — no runtime coupling to the layout module.
import type { ViewportTier } from '../components/websiteAgentLayout';

/** A restored turn. Structurally a `TranscriptMessage` minus the live-only bits. */
export interface PersistedMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Pure core — no storage, no DOM. The driver below is a thin wrapper.
// ---------------------------------------------------------------------------

/**
 * Normalize + cap an arbitrary message list for persistence.
 *
 * Deliberately lossy: `isPartial` (a live streaming flag, meaningless once
 * reloaded) and `sources` (citation chips the website chat never renders, and
 * the biggest field on the object) are dropped. Anything that isn't a renderable
 * turn — wrong role, empty/non-string content — is dropped rather than persisted
 * as a bubble that would render blank.
 */
export function capTranscript(messages: unknown): PersistedMessage[] {
  if (!Array.isArray(messages)) return [];
  const out: PersistedMessage[] = [];
  for (const raw of messages) {
    if (!raw || typeof raw !== 'object') continue;
    const m = raw as Record<string, unknown>;
    const role = m.role === 'user' || m.role === 'assistant' ? m.role : null;
    if (!role) continue;
    if (typeof m.content !== 'string') continue;
    const content = m.content.slice(0, MAX_PERSISTED_CONTENT_CHARS);
    if (!content.trim()) continue;
    const timestamp =
      typeof m.timestamp === 'number' && Number.isFinite(m.timestamp) ? m.timestamp : 0;
    // The id is ASSIGNED here, never carried: see parseTranscript.
    out.push({ id: '', role, content, timestamp });
  }
  // Drop the OLDEST — the tail is the context worth keeping.
  return withRestoredIds(out.slice(-MAX_PERSISTED_MESSAGES));
}

/**
 * Stamp positional ids on restored turns.
 *
 * Restored bubbles share a React list with LIVE ones ({@link PersistedMessage}s
 * are rendered before `transcriptHistory`), and the SDK mints live ids as
 * `user-<epoch>` / `assistant-<epoch>`. Carrying the stored ids forward would
 * (a) risk a duplicate key against a live message minted in the same millisecond
 * on a later page load, and (b) require a prefix that grows on every save/restore
 * round trip. Positional ids are stable for the life of a page load — the
 * restored list never reorders — and cannot collide with the live namespace.
 */
function withRestoredIds(messages: PersistedMessage[]): PersistedMessage[] {
  return messages.map((m, i) => ({ ...m, id: `wa_prior_${i}` }));
}

/** The exact string persisted under {@link WA_TRANSCRIPT_KEY} (`''` = nothing). */
export function serializeTranscript(messages: unknown): string {
  const capped = capTranscript(messages);
  if (capped.length === 0) return '';
  try {
    return JSON.stringify(capped);
  } catch {
    // Belt: capTranscript already rebuilt plain {id,role,content,timestamp}
    // objects, so a cycle or an exotic value can't reach here — but this runs
    // inside a render effect, where "no history" is a far better failure than a
    // thrown error, so the guarantee is made structural rather than assumed.
    return '';
  }
}

/**
 * Parse a persisted payload back into renderable turns.
 *
 * Treats the payload as hostile: it is JSON in a store the host page (and the
 * visitor's devtools) can write anything into. Junk, a wrong shape, a
 * hand-edited 10,000-message array — all collapse to "no history", or to the
 * capped, normalized tail.
 */
export function parseTranscript(raw: unknown): PersistedMessage[] {
  if (typeof raw !== 'string' || !raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  return capTranscript(parsed);
}

/**
 * Should a persisted "open" flag actually re-open the panel on THIS page load?
 *
 * Not below the rail tier. Under 768px the sidebar is a full-screen takeover
 * (`railLayout`'s 'fullscreen' branch), so restoring it would cover the page a
 * mobile visitor just navigated to — on every single page — with the site behind
 * an unrequested chat. A phone gets the launcher back, and the transcript is
 * still restored, so opening it picks the conversation up exactly where it was.
 * (Consequence, deliberate: the open flag is then rewritten as closed by the
 * persistence effect, i.e. an open panel does not survive navigation on mobile
 * at all — which is the right contract for a surface that behaves as a modal.)
 *
 * The overlay tier (768–1023px) DOES restore: the rail sits over one edge of a
 * page that stays visible and dismissible, which is the Qualified behaviour.
 */
export function shouldRestoreOpen(storedOpen: boolean, tier: ViewportTier): boolean {
  return storedOpen && tier !== 'fullscreen';
}

/**
 * The lazy-start truth table: what a visitor interaction does when the rail was
 * RESTORED and no session is live yet (`sessionStarted === false`).
 *
 *   'message' — a composer send, a suggestion chip, a nudge chip, the demo CTA.
 *               Start AND forward: `startWebsiteAgentSession()` flips its guard
 *               synchronously (before its first await), so the send that follows
 *               is QUEUED by sendOrQueueClientMessage and flushed at bot-ready
 *               (the FLO-382 mechanism) rather than dropped.
 *   'mic'     — start ONLY. There is no PipecatClient yet, so forwarding the
 *               toggle would be a no-op. WebsiteAgentView instead marks that
 *               start with `requestMicrophone:true`; the SDK requests access
 *               from this gesture and joins enabled when granted, so the first
 *               explicit voice click is still one action.
 *
 * Once a session exists, every interaction is a plain forward — this is inert.
 * The speaker toggle is deliberately absent: it is a preference, not engagement,
 * and must never spend a session (the rail hides it while dormant anyway,
 * because `startWebsiteAgentSession()` resets `speakerMuted` and a control whose
 * state is about to be overwritten is a lying control).
 */
export function lazyStartAction(
  kind: 'message' | 'mic',
  sessionStarted: boolean,
): { start: boolean; forward: boolean } {
  if (sessionStarted) return { start: false, forward: true };
  return { start: true, forward: kind === 'message' };
}

// ---------------------------------------------------------------------------
// Storage driver — every entry point swallows its own failures.
// ---------------------------------------------------------------------------

function readKey(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // absent (SSR/node), blocked (privacy mode), or throwing
  }
}

function writeKey(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* quota exceeded / blocked — continuity is best-effort, never load-bearing */
  }
}

function removeKey(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* nothing to do — see writeKey */
  }
}

/** Was the sidebar open when this tab last painted it? */
export function readOpenState(): boolean {
  return readKey(WA_OPEN_KEY) === WA_OPEN_VALUE;
}

/**
 * Persist the open/closed state. Closing REMOVES the key rather than writing a
 * falsy value: a sidebar the visitor deliberately closed must not resurrect on
 * the next page, and absence is the same thing a first-ever visit reads.
 */
export function writeOpenState(open: boolean): void {
  if (open) writeKey(WA_OPEN_KEY, WA_OPEN_VALUE);
  else removeKey(WA_OPEN_KEY);
}

/** The persisted turns, normalized + capped. Never throws, never null. */
export function readTranscript(): PersistedMessage[] {
  return parseTranscript(readKey(WA_TRANSCRIPT_KEY));
}

/**
 * Write an already-serialized payload ({@link serializeTranscript}). Split from
 * `writeTranscript` so the caller can compare payloads and skip the store
 * entirely when nothing changed — the render that produces this runs at
 * streaming cadence.
 */
export function persistTranscriptPayload(payload: string): void {
  if (!payload) removeKey(WA_TRANSCRIPT_KEY);
  else writeKey(WA_TRANSCRIPT_KEY, payload);
}

/** Serialize + persist in one step. */
export function writeTranscript(messages: unknown): void {
  persistTranscriptPayload(serializeTranscript(messages));
}

/** The session/conversation id that produced the persisted transcript. */
export function readConversationId(): string | null {
  const v = readKey(WA_CONVERSATION_KEY);
  return v && v.trim() ? v : null;
}

/** Pin the conversation id. Junk/empty clears it rather than storing garbage. */
export function writeConversationId(id: unknown): void {
  if (typeof id !== 'string' || !id.trim()) removeKey(WA_CONVERSATION_KEY);
  else writeKey(WA_CONVERSATION_KEY, id);
}

/** The thread the visitor ended, if this tab ended one ({@link WA_FORGOTTEN_KEY}). */
export function readForgottenConversationId(): string | null {
  const v = readKey(WA_FORGOTTEN_KEY);
  return v && v.trim() ? v : null;
}

/** Drop the marker — the visitor has moved on to a different thread. */
export function clearForgottenConversation(): void {
  removeKey(WA_FORGOTTEN_KEY);
}

/**
 * "Forget this chat": drop the transcript + its conversation id, keep the
 * open/closed state (ending a chat does not close the panel — it lands on the
 * friendly ended state).
 *
 * The id is REMEMBERED as it is dropped ({@link WA_FORGOTTEN_KEY}). Forgetting
 * it locally is not enough: the server resumes by visitor, so the next open
 * returns this same conversation and would paint its turns back — "forget this
 * chat" would last exactly until the next tap, and survive a reload not at all.
 */
export function clearConversationContinuity(): void {
  const forgotten = readConversationId();
  if (forgotten) writeKey(WA_FORGOTTEN_KEY, forgotten);
  removeKey(WA_TRANSCRIPT_KEY);
  removeKey(WA_CONVERSATION_KEY);
}

/** Drop every continuity key (teardown / hard reset). */
export function clearContinuity(): void {
  clearConversationContinuity();
  // A hard reset is not a "forget this chat" — it leaves nothing behind at all,
  // including the marker the line above just wrote.
  clearForgottenConversation();
  removeKey(WA_OPEN_KEY);
}
